/**
 * kx-cart-groups — builder builds in the cart (tester round 2).
 *
 * A build is the cart lines that share a _kut_group property: the tank (the
 * one line without _tank_addon) and its add-ons (_tank_addon lines: paint,
 * fittings, pump). The cart drawer, the cart page and the header cart
 * dropdown show each build as the tank with its add-ons under it, with ONE
 * quantity and ONE remove for the whole build. These helpers hold the rules
 * so all three agree; the Liquid in snippets/cart-drawer.liquid and
 * sections/main-cart-items.liquid applies the same classification on the
 * server (.catalog-source/test/cart-*.test.js checks they match).
 *
 * Works for both cart shapes:
 *  - flat carts (today): the lines of a build can sit anywhere in the cart,
 *    so a build is found by _kut_group alone, never by line order;
 *  - Shopify nested lines (parent_id on /cart/add.js, behind the builders'
 *    switch): add-ons carry parent_relationship and Shopify removes them with
 *    their tank, so a removal leaves their keys out (re-posting a key that is
 *    already gone can fail the whole request).
 *
 * Pure: no DOM, no fetch. Rows are /cart.js items, or plain objects read from
 * the rendered cart ({ key, group, role, parentKey }), see rowsFromElements.
 * UMD: window.KxCartGroups in the browser, module.exports in node.
 */
(function (root) {
  'use strict';

  if (root && root.KxCartGroups && root.KxCartGroups.version === 1) return;

  /** Properties an add-on line repeats from its tank: shown on the tank row only. */
  var CHILD_HIDDEN_PROPS = ['Addon', 'Region', 'Type', 'Colour'];

  function propsOf(item) {
    return (item && item.properties) || {};
  }
  function filled(v) {
    return v !== undefined && v !== null && String(v).trim() !== '';
  }
  /** The parent line key of a nested line (Ajax JSON parent_key, or a Liquid-like parent object). */
  function parentKeyOf(item) {
    var pr = item && item.parent_relationship;
    if (!pr) return null;
    return pr.parent_key || (pr.parent && pr.parent.key) || null;
  }

  /**
   * Classify cart items, in cart order. Each entry:
   *   { key, index (0-based cart position), group, role, parentKey, item }
   * role:
   *   'tank'   the first line of a group that is not an add-on (and not nested);
   *   'child'  an add-on whose group has a tank in the cart (shown under it);
   *   'orphan' an add-on whose tank is no longer in the cart (shown on its own,
   *            with its own quantity and remove, so it can be taken out);
   *   'line'   anything else (non-builder lines render as before).
   */
  function classify(items) {
    var list = Array.isArray(items) ? items : [];
    var heads = {};
    list.forEach(function (it) {
      var p = propsOf(it);
      if (filled(p._kut_group) && !filled(p._tank_addon) && !parentKeyOf(it)) heads[p._kut_group] = true;
    });
    var done = {};
    return list.map(function (it, i) {
      var p = propsOf(it);
      var group = filled(p._kut_group) ? String(p._kut_group) : null;
      var role = 'line';
      if (group) {
        if (!filled(p._tank_addon) && !parentKeyOf(it)) {
          if (!done[group]) {
            role = 'tank';
            done[group] = true;
          }
        } else if (filled(p._tank_addon)) {
          role = heads[group] ? 'child' : 'orphan';
        }
      }
      return { key: it && it.key, index: i, group: group, role: role, parentKey: parentKeyOf(it), item: it };
    });
  }

  /**
   * Display order: every line in cart order, except that each build's add-ons
   * come straight after their tank (in cart order) wherever they sit in the
   * cart. Same entries as classify(), plus depth (0, or 1 for an add-on).
   */
  function displayOrder(items) {
    var entries = classify(items);
    var out = [];
    entries.forEach(function (e) {
      if (e.role === 'child') return;
      out.push(Object.assign({ depth: 0 }, e));
      if (e.role === 'tank') {
        entries.forEach(function (c) {
          if (c.role === 'child' && c.group === e.group) out.push(Object.assign({ depth: 1 }, c));
        });
      }
    });
    return out;
  }

  /** Normalise rows: classified entries, /cart.js items, or DOM rows ({key, group, role, parentKey}). */
  function asRows(rows) {
    var list = Array.isArray(rows) ? rows : [];
    if (list.length && list[0] && list[0].role === undefined) return classify(list);
    return list;
  }

  /**
   * The /cart/update.js `updates` for setting a build's quantity: the tank and
   * every one of its add-ons, keyed by line key (never by position), all to
   * `quantity`. quantity 0 removes the build: add-ons nested under this tank
   * are left out (Shopify removes them with it); flat ones are zeroed with it.
   * Returns null when tankKey is not a build's tank (the caller then changes
   * that one line, as Dawn does).
   */
  function buildUpdates(rows, tankKey, quantity) {
    var list = asRows(rows);
    var tank = null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].role === 'tank' && list[i].key === tankKey) {
        tank = list[i];
        break;
      }
    }
    if (!tank) return null;
    var q = Math.max(0, Math.floor(Number(quantity) || 0));
    var updates = {};
    updates[tank.key] = q;
    list.forEach(function (r) {
      if (r.role !== 'child' || r.group !== tank.group || !r.key) return;
      if (q === 0 && r.parentKey && r.parentKey === tank.key) return;
      updates[r.key] = q;
    });
    return updates;
  }

  /**
   * What removing one line from a cart takes out, as /cart/update.js updates:
   * a tank takes its whole build; an add-on under a tank is not removable on
   * its own (null: the caller shows no remove for it); anything else is just
   * that line.
   */
  function removalUpdates(items, key) {
    var list = asRows(items);
    var row = null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === key) {
        row = list[i];
        break;
      }
    }
    if (!row) return null;
    if (row.role === 'tank') return buildUpdates(list, key, 0);
    if (row.role === 'child') return null;
    var one = {};
    one[key] = 0;
    return one;
  }

  /** The add-on rows of a build (cart order). */
  function childrenOf(rows, group) {
    return asRows(rows).filter(function (r) {
      return r.role === 'child' && r.group === group;
    });
  }

  /**
   * A build's total (cents, after line discounts) and add-on count, from
   * /cart.js items: what the tank row shows as "Build total".
   */
  function buildTotal(items, group) {
    var total = 0;
    var addons = 0;
    classify(items).forEach(function (e) {
      if (e.group !== group || (e.role !== 'tank' && e.role !== 'child')) return;
      total += Number(e.item.final_line_price) || 0;
      if (e.role === 'child') addons++;
    });
    return { total: total, addons: addons };
  }

  /** Sum (cents) of every line-level discount allocation: the footer's "Offers applied". */
  function offersApplied(items) {
    return (Array.isArray(items) ? items : []).reduce(function (sum, it) {
      return (
        sum +
        ((it && it.line_level_discount_allocations) || []).reduce(function (s, a) {
          return s + (Number(a && a.amount) || 0);
        }, 0)
      );
    }, 0);
  }

  /** The title an add-on line shows: its Addon property (the sku-map title), else the variant / product title. */
  function childTitle(item) {
    var p = propsOf(item);
    if (filled(p.Addon)) return String(p.Addon);
    return String((item && (item.variant_title || item.product_title || item.title)) || '');
  }

  /** The public properties an add-on row still shows (not _private, not repeated from the tank). */
  function childProps(item) {
    var p = propsOf(item);
    return Object.keys(p)
      .filter(function (k) {
        return k.charAt(0) !== '_' && filled(p[k]) && CHILD_HIDDEN_PROPS.indexOf(k) === -1;
      })
      .map(function (k) {
        return [k, String(p[k])];
      });
  }

  /**
   * Rows read from rendered cart rows (elements carrying data-kx-role,
   * data-kx-key, data-kx-group and, on a nested add-on, data-kx-parent-key).
   */
  function rowsFromElements(elements) {
    var out = [];
    for (var i = 0; i < (elements ? elements.length : 0); i++) {
      var el = elements[i];
      var d = el && el.dataset;
      if (!d || !d.kxRole) continue;
      out.push({ key: d.kxKey || null, group: d.kxGroup || null, role: d.kxRole, parentKey: d.kxParentKey || null });
    }
    return out;
  }

  var api = {
    version: 1,
    CHILD_HIDDEN_PROPS: CHILD_HIDDEN_PROPS,
    classify: classify,
    displayOrder: displayOrder,
    buildUpdates: buildUpdates,
    removalUpdates: removalUpdates,
    childrenOf: childrenOf,
    buildTotal: buildTotal,
    offersApplied: offersApplied,
    childTitle: childTitle,
    childProps: childProps,
    rowsFromElements: rowsFromElements,
    parentKeyOf: parentKeyOf,
  };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.KxCartGroups = api;
})(typeof window !== 'undefined' ? window : globalThis);
